'use client';

import { get } from 'lodash';

import { ConditionalWrapper } from '@/components/conditional-wrapper';
import { Tooltip } from '@/components/tooltip';
import { TooltipContentProps } from '@/components/tooltip/tooltip-content';
import { colors } from '@/theme/constants';
import { MarkerSize } from '@/types';
import { PlaceInterface } from '@/types/api-types';
import { tv } from '@/utils/styles';

export interface MarkerDotProps {
  isActive?: boolean;
  isSelected?: boolean;
  isOffset?: boolean;
  showTooltip?: boolean;
  tooltipInitialOpen?: boolean;
  tooltipContent?: React.ReactNode;
  size?: MarkerSize;
  type?: PlaceInterface['type'];
  children?: React.ReactNode;
  tooltipVariant?: TooltipContentProps['variant'];
  className?: string;
  zIndex?: string | number;
  backgroundColor?: string;
  borderColor?: string;
  pulsingBorderVariant?: 'light' | 'dark';
}

const MarkerDot = ({
  isActive,
  isSelected,
  isOffset = false,
  showTooltip,
  tooltipContent,
  size = 'lg',
  zIndex,
  children,
  tooltipVariant = 'blue',
  className,
  backgroundColor,
  borderColor,
  type,
  pulsingBorderVariant = 'light',
  ...props
}: MarkerDotProps) => {
  const { base, pulsingBorder } = markerDotStyles({ type, size, isOffset, isActive, isSelected, pulsingBorderVariant });

  const bgColor = backgroundColor && get(colors, backgroundColor, backgroundColor);

  return (
    <ConditionalWrapper
      condition={Boolean(tooltipContent)}
      // eslint-disable-next-line react/no-unstable-nested-components
      wrapper={(content) => (
        <Tooltip
          shouldUsePortal={false}
          content={tooltipContent}
          isOpen={showTooltip}
          variant={tooltipVariant}
          whileElementsMountedOptions={{
            animationFrame: true,
          }}
        >
          {content}
        </Tooltip>
      )}
    >
      <div
        className={base({ className })}
        style={{ backgroundColor: bgColor, borderColor, zIndex }}
        data-cy="marker-dot"
        {...props}
      >
        {children}
        <span className={pulsingBorder()} />
      </div>
    </ConditionalWrapper>
  );
};

const markerDotStyles = tv({
  slots: {
    base: 'relative z-4 flex content-center items-center justify-center rounded-full border border-black/30 bg-grey-400 text-white shadow-[0_0_0_1px_rgba(255,255,255,1)] transition-colors duration-200 ease-in-out hover:z-8 [&_svg]:block [&_svg]:h-2.5 [&_svg]:w-2.5 [&_svg]:transition-all',
    pulsingBorder: 'absolute left-0 top-0 z-0 hidden h-full w-full rounded-full',
  },
  variants: {
    type: {
      airport: {
        base: 'border border-solid !border-[#78a73e]',
      },
      seaport: {
        base: 'border border-solid !border-lightBlue-500',
      },
      unknown: {
        base: 'border border-solid !border-grey-400',
      },
      address: null,
      carrier: null,
      city: null,
      country: null,
      locality: null,
      place: null,
      premise: null,
      region: null,
      trainStation: null,
      vehicle: null,
    },
    size: {
      xs: 'h-2 w-2',
      sm: 'h-[9px] w-[9px]',
      md: 'h-3 w-3',
      lg: 'h-4 w-4',
      xl: 'h-5 w-5',
      '2xl': 'h-6 w-6 [&_svg]:h-3.5 [&_svg]:w-3.5',
      '3xl': 'h-8 w-8 [&_svg]:h-[19px] [&_svg]:w-[19px]',
    },
    isActive: {
      true: {
        pulsingBorder: 'block',
      },
    },
    isSelected: {
      true: {
        base: 'z-9',
      },
    },
    isOffset: {
      true: null,
      false: null,
    },
    pulsingBorderVariant: {
      light: {
        pulsingBorder: 'animate-light-blue-pulse',
      },
      dark: {
        pulsingBorder: 'animate-dark-blue-pulse',
      },
    },
  },
  compoundVariants: [
    {
      size: 'xs',
      isOffset: true,
      class: '-ml-1 -mt-1',
    },
    {
      size: 'sm',
      isOffset: true,
      class: '-ml-[4.5px] -mt-[4.5px]',
    },
    {
      size: 'md',
      isOffset: true,
      class: '-ml-1.5 -mt-1.5',
    },
    {
      size: 'lg',
      isOffset: true,
      class: '-ml-2 -mt-2',
    },
    {
      size: 'xl',
      isOffset: true,
      class: '-ml-2.5 -mt-2.5',
    },
    {
      size: '2xl',
      isOffset: true,
      class: '-ml-3 -mt-3',
    },
    {
      size: '3xl',
      isOffset: true,
      class: '-ml-4 -mt-4',
    },
  ],
});

export { MarkerDot };
